import { FilesPanel } from '@odo/shared-ui';
import type { FilesCapabilities } from '@odo/shared-ui';
import React, { useCallback, useMemo } from 'react';
import apiClient from '../../api/config';
import { usePatientAssets } from '../../hooks/usePatientAssets';
import { usePatientFilesApi } from '../../hooks/usePatientFilesApi';
import { useUser } from '../../providers/UserProvider';
import { createCbctScanStorage } from '../../services/cbctScanStorage';
import { reportError } from '../../services/errorReporting';
import { uploadFileToGCS } from '../../services/patientFiles';

interface FilesTabContentProps {
    patientId: string;
    /** Shown (with the scan's upload date) in the CBCT viewer's top bar. */
    patientName?: string;
    initialPreviewFileIds?: string[] | null;
    onPreviewChange?: (fileIds: string[] | null) => void;
}

/**
 * files-viewer wiring for the shared FilesPanel: asset fetching (react-query),
 * the FilesApi implementation, admin-gated capabilities, Sentry-style upload
 * error reporting, CBCT marking storage, CBCT export upload and the CBCT
 * processing trigger all live app-side and flow in through the panel's seams.
 *
 * IMPORTANT: work-queue task hosts render this UNCONTROLLED (no preview props).
 * The shared panel treats an absent `initialPreviewFileIds` as "never
 * reconcile" - do not default it here.
 */
const FilesTabContent: React.FC<FilesTabContentProps> = ({
    patientId,
    patientName,
    initialPreviewFileIds,
    onPreviewChange
}) => {
    const {
        assets,
        isLoading: isAssetsLoading,
        error: assetsError,
        refresh: refreshAssets
    } = usePatientAssets(patientId);
    const { user } = useUser();
    const isAdmin = user?.database.data.isAdmin ?? false;

    // Stable per-patient persistence handle for CBCT markings - the viewer
    // re-loads whenever this identity changes, so it must be memoized.
    const cbctStorage = useMemo(() => createCbctScanStorage(patientId), [patientId]);

    const patientFilesApi = usePatientFilesApi(patientId);

    const capabilities = useMemo<FilesCapabilities>(
        () => ({
            canDelete: true,
            canRename: true,
            canReclassify: isAdmin,
            showPreviewActions: true,
            canSetProfilePicture: isAdmin,
            cbctReadOnly: false,
            showUploadedBy: true,
            enablePreviewNavigation: true,
            tooltips: 'radix',
            onUploadError: (error, ctx) => {
                const message = error instanceof Error ? error.message : String(error);
                reportError({
                    source: 'files-viewer',
                    message: `File upload failed: ${message}`,
                    stack: ctx.errorStack,
                    url: window.location.href,
                    pathname: window.location.pathname,
                    userAgent: navigator.userAgent,
                    errorType: 'upload_failure',
                    metadata: {
                        patientId,
                        fileName: ctx.fileName,
                        fileSize: ctx.fileSize,
                        context: 'files_tab'
                    }
                });
            }
        }),
        [isAdmin, patientId]
    );

    const handleCbctExport = useCallback(
        async (png: Blob, info: { mode: 'pano' | 'ceph'; filename: string; scanId?: string }) => {
            const file = new File([png], info.filename, { type: png.type || 'image/png' });

            const metadata = info.scanId ? { cbct: info.scanId } : undefined;

            const result = await uploadFileToGCS(
                patientId,
                file,
                undefined,
                metadata,
                'RADIOGRAPH'
            );
            if (!result.success) throw new Error(result.error ?? 'Upload failed');

            if (!result.patientFileId) return undefined;
            return `${window.location.origin}/patients/${patientId}?tab=files&preview=${encodeURIComponent(result.patientFileId)}`;
        },
        [patientId]
    );

    return (
        <FilesPanel
            patientId={patientId}
            patientName={patientName}
            assets={assets}
            isLoading={isAssetsLoading}
            error={assetsError}
            refresh={refreshAssets}
            api={patientFilesApi}
            capabilities={capabilities}
            initialPreviewFileIds={initialPreviewFileIds}
            onPreviewChange={onPreviewChange}
            cbctScanStorage={cbctStorage}
            onCbctExport={handleCbctExport}
            viewerApiBaseUrl={apiClient.defaults.baseURL ?? ''}
            getAuthToken={() => localStorage.getItem('odo_login_token')}
            storageKeyPrefix="files_viewer"
        />
    );
};

export { FilesTabContent };
